import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicProfile, trackView, trackLinkClick, subscribeToProfile, verifyLinkPassword } from '../services/api';
import '../styles/global.css';
import '../styles/Profile.css';

const getLinkIcon = (url?: string): string => {
  if (!url) return '🔗';
  try {
    const host = new URL(url).hostname.replace('www.', '');
    if (host.includes('youtube.com') || host.includes('youtu.be')) return '▶️';
    if (host.includes('instagram.com'))  return '📸';
    if (host.includes('twitter.com') || host.includes('x.com')) return '𝕏';
    if (host.includes('linkedin.com'))   return '💼';
    if (host.includes('github.com'))     return '🐙';
    if (host.includes('facebook.com'))   return '📘';
    if (host.includes('tiktok.com'))     return '🎵';
    if (host.includes('t.me') || host.includes('telegram'))    return '✈️';
    if (host.includes('wa.me') || host.includes('whatsapp'))   return '💬';
    if (host.includes('discord.gg') || host.includes('discord.com')) return '🎮';
    if (host.includes('threads.net'))    return '🧵';
    if (host.includes('pinterest.com'))  return '📌';
    if (host.includes('twitch.tv'))      return '🟣';
    if (host.includes('spotify.com'))    return '🎧';
    if (host.includes('snapchat.com'))   return '👻';
    if (host.includes('reddit.com'))     return '🤖';
    if (host.includes('amazon.'))        return '🛒';
    if (host.includes('flipkart.com'))   return '🛍️';
    if (host.includes('buymeacoffee.com') || host.includes('ko-fi.com')) return '☕';
    if (host.includes('patreon.com'))    return '🎁';
    if (host.includes('gumroad.com'))    return '💳';
    if (host.includes('notion.so') || host.includes('notion.site')) return '📝';
    if (host.includes('medium.com'))     return '✍️';
    if (host.includes('substack.com'))   return '📧';
    if (host.includes('behance.net'))    return '🎨';
    if (host.includes('dribbble.com'))   return '🏀';
    if (host.includes('figma.com'))      return '🖌️';
    if (host.includes('calendly.com'))   return '📅';
    if (host.includes('zoom.us'))        return '📹';
    if (host.includes('meet.google'))    return '📹';
    if (host.includes('forms.gle') || host.includes('docs.google')) return '📄';
    if (host.includes('razorpay.com') || host.includes('paypal.com') || host.includes('upi')) return '💰';
    if (host.includes('play.google.com')) return '📱';
    if (host.includes('apps.apple.com')) return '🍎';
    return '🔗';
  } catch {
    return '🔗';
  }
};

interface Link {
  _id: string;
  title: string;
  url: string;
  icon: string;
  isProtected?: boolean;
  thumbnail?: string;
  isFeatured?: boolean;
}

interface SocialLinks {
  instagram?: string;
  twitter?: string;
  youtube?: string;
  linkedin?: string;
  github?: string;
  facebook?: string;
  tiktok?: string;
  telegram?: string;
  whatsapp?: string;
  discord?: string;
  threads?: string;
  pinterest?: string;
  twitch?: string;
  spotify?: string;
  snapchat?: string;
  reddit?: string;
}

interface User {
  name: string;
  username: string;
  avatar: string;
  bio: string;
  socialLinks?: SocialLinks;
  theme?: string;
  badge?: string;
  sensitiveContent?: boolean;
  hideBranding?: boolean;
  customTheme?: {
    bg?: string;
    text?: string;
    accent?: string;
    font?: string;
  };
}

const SOCIAL_META: { key: keyof SocialLinks; label: string; icon: string }[] = [
  { key: 'instagram', label: 'Instagram', icon: '📸' },
  { key: 'twitter',   label: 'Twitter/X',  icon: '𝕏' },
  { key: 'youtube',   label: 'YouTube',    icon: '▶️' },
  { key: 'linkedin',  label: 'LinkedIn',   icon: '💼' },
  { key: 'github',    label: 'GitHub',     icon: '🐙' },
  { key: 'facebook',  label: 'Facebook',   icon: '📘' },
  { key: 'tiktok',    label: 'TikTok',     icon: '🎵' },
  { key: 'telegram',  label: 'Telegram',   icon: '✈️' },
  { key: 'whatsapp',  label: 'WhatsApp',   icon: '💬' },
  { key: 'discord',   label: 'Discord',    icon: '🎮' },
  { key: 'threads',   label: 'Threads',    icon: '🧵' },
  { key: 'pinterest', label: 'Pinterest',  icon: '📌' },
  { key: 'twitch',    label: 'Twitch',     icon: '🟣' },
  { key: 'spotify',   label: 'Spotify',    icon: '🎧' },
  { key: 'snapchat',  label: 'Snapchat',   icon: '👻' },
  { key: 'reddit',    label: 'Reddit',     icon: '🤖' },
];

const THEMES: Record<string, React.CSSProperties> = {
  noir: {
    background: '#1C1208', color: '#F5F0E8',
    '--accent': '#C0392B', '--muted': '#8a7a6a', '--border': '#2e2416',
    '--bg-alt': '#241a0e', '--link-bg': '#2a1e10', '--link-hover': '#3a2e20',
    fontFamily: 'Georgia, serif',
  } as React.CSSProperties,
  ivory: {
    background: '#F5F0E8', color: '#1C1208',
    '--accent': '#C0392B', '--muted': '#7a6a5a', '--border': '#e0d8cc',
    '--bg-alt': '#ede8e0', '--link-bg': '#ede8e0', '--link-hover': '#e0d8cc',
    fontFamily: 'Georgia, serif',
  } as React.CSSProperties,
  ocean: {
    background: 'linear-gradient(135deg, #0A1628 0%, #1A3A5C 100%)', color: '#E8F4FD',
    '--accent': '#00BCD4', '--muted': '#7aaccc', '--border': '#1a3a5c',
    '--bg-alt': 'rgba(255,255,255,0.05)', '--link-bg': 'rgba(255,255,255,0.08)', '--link-hover': 'rgba(255,255,255,0.14)',
    fontFamily: "'Courier New', monospace",
  } as React.CSSProperties,
  forest: {
    background: 'linear-gradient(135deg, #0D1F0D 0%, #1A3320 100%)', color: '#E8F5E9',
    '--accent': '#66BB6A', '--muted': '#7aaa7a', '--border': '#1a3320',
    '--bg-alt': 'rgba(255,255,255,0.05)', '--link-bg': 'rgba(255,255,255,0.08)', '--link-hover': 'rgba(255,255,255,0.14)',
    fontFamily: 'Georgia, serif',
  } as React.CSSProperties,
  sunset: {
    background: 'linear-gradient(135deg, #1A0A0A 0%, #3D1515 100%)', color: '#FFF0E8',
    '--accent': '#FF6B6B', '--muted': '#cc8a7a', '--border': '#3d1515',
    '--bg-alt': 'rgba(255,255,255,0.05)', '--link-bg': 'rgba(255,255,255,0.08)', '--link-hover': 'rgba(255,255,255,0.14)',
    fontFamily: 'Georgia, serif',
  } as React.CSSProperties,
  minimal: {
    background: '#FFFFFF', color: '#111111',
    '--accent': '#111111', '--muted': '#888888', '--border': '#eeeeee',
    '--bg-alt': '#f5f5f5', '--link-bg': '#f5f5f5', '--link-hover': '#eeeeee',
    fontFamily: "'Helvetica Neue', sans-serif",
  } as React.CSSProperties,
};

const PublicProfile = () => {
  const { username } = useParams();
  const [user, setUser]       = useState<User | null>(null);
  const [links, setLinks]     = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [subEmail, setSubEmail]   = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subMsg, setSubMsg]       = useState('');
  const [notFound, setNotFound]   = useState(false);
  // Password modal state
  const [pwdModal, setPwdModal]   = useState<{ linkId: string; title: string } | null>(null);
  const [pwdInput, setPwdInput]   = useState('');
  const [pwdError, setPwdError]   = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [sensitiveWarning, setSensitiveWarning] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getPublicProfile(username!);
        setUser(res.data.user);
        setLinks(res.data.links);
        if (res.data.user?.sensitiveContent) setSensitiveWarning(true);
        await trackView(username!);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  if (loading) return (
    <div className="profile-loading">
      <p style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)', fontSize: 13 }}>Loading...</p>
    </div>
  );

  if (notFound) return (
    <div className="profile-notfound">
      <div className="notfound-box">
        <div className="notfound-num">404</div>
        <div className="notfound-title">User not found</div>
        <div className="notfound-sub">@{username} doesn't exist</div>
      </div>
    </div>
  );

  const activeSocials = SOCIAL_META.filter(s => user?.socialLinks?.[s.key]);

  // Theme logic — custom theme override karta hai preset themes ko
  const themeStyle: React.CSSProperties = user?.customTheme?.bg
    ? {
        background: user.customTheme.bg,
        color: user.customTheme.text || '#ffffff',
        '--accent': user.customTheme.accent || '#ffffff',
        '--muted': 'rgba(255,255,255,0.5)',
        '--border': 'rgba(255,255,255,0.1)',
        '--bg-alt': 'rgba(255,255,255,0.05)',
        '--link-bg': 'rgba(255,255,255,0.08)',
        '--link-hover': 'rgba(255,255,255,0.14)',
        fontFamily: user.customTheme.font || 'Georgia, serif',
      } as React.CSSProperties
    : THEMES[user?.theme || 'noir'] || THEMES.noir;

  // Sensitive content warning
  if (sensitiveWarning) return (
    <div style={{
      minHeight: '100vh', background: '#111',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Georgia, serif', padding: 20,
    }}>
      <div style={{
        background: '#1a1a1a', border: '1px solid #333',
        borderRadius: 20, padding: 40, maxWidth: 380,
        textAlign: 'center', color: '#fff',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔞</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>
          Sensitive Content
        </div>
        <div style={{ fontSize: 14, color: '#888', lineHeight: 1.7, marginBottom: 28 }}>
          This profile may contain content that is intended for adults only. Please confirm your age to continue.
        </div>
        <button
          onClick={() => setSensitiveWarning(false)}
          style={{
            width: '100%', padding: '12px', borderRadius: 10,
            background: '#C0392B', color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10,
          }}
        >
          I am 18+ — Continue
        </button>
        <button
          onClick={() => window.history.back()}
          style={{
            width: '100%', padding: '12px', borderRadius: 10,
            background: 'transparent', color: '#555',
            border: '1px solid #333', fontSize: 14, cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="profile-page" style={themeStyle}>
      <div className="profile-inner">

        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} className="profile-avatar-img" />
            ) : (
              user?.name.charAt(0).toUpperCase()
            )}
          </div>
          <div className="profile-name" style={{ color: 'inherit' }}>{user?.name}</div>
          <div className="profile-username" style={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            @{user?.username}
            {user?.badge === 'verified' && <span title="Verified" style={{ fontSize: 15 }}>✅</span>}
            {user?.badge === 'creator'  && <span title="Creator"  style={{ fontSize: 15 }}>🎨</span>}
            {user?.badge === 'pro'      && <span title="Pro"      style={{ fontSize: 13, background: 'var(--accent)', color: '#fff', padding: '1px 7px', borderRadius: 20, fontWeight: 700 }}>PRO</span>}
          </div>
          {user?.bio && <div className="profile-bio" style={{ color: 'var(--muted)' }}>{user.bio}</div>}

          {activeSocials.length > 0 && (
            <div className="profile-socials">
              {activeSocials.map(({ key, label, icon }) => (
                <a key={key} href={user?.socialLinks?.[key]} target="_blank" rel="noopener noreferrer"
                  className="profile-social-btn"
                  style={{ background: 'var(--link-bg)', color: 'inherit', borderColor: 'var(--border)' }}
                >
                  <span>{icon}</span> {label}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Links */}
        <div>
          {links.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>No links yet</p>
          ) : (
            links.map((link) => (
              <a key={link._id} href={link.url} target="_blank" rel="noopener noreferrer"
                className="profile-link"
                style={{
                  background: link.isFeatured ? 'var(--accent)' : 'var(--link-bg)',
                  color: link.isFeatured ? '#fff' : 'inherit',
                  borderColor: link.isFeatured ? 'var(--accent)' : 'var(--border)',
                  boxShadow: link.isFeatured ? '0 4px 20px rgba(0,0,0,0.2)' : undefined,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onClick={async (e) => {
                  e.preventDefault();
                  if (link.isProtected) {
                    const unlocked = sessionStorage.getItem(`unlocked_${link._id}`);
                    if (unlocked === 'true') {
                      try { await trackLinkClick(link._id); } catch {}
                      window.open(link.url, '_blank');
                    } else {
                      setPwdInput('');
                      setPwdError('');
                      setPwdModal({ linkId: link._id, title: link.title });
                    }
                  } else {
                    try { await trackLinkClick(link._id); } catch {}
                    window.open(link.url, '_blank');
                  }
                }}
              >
                {/* Thumbnail */}
                {link.thumbnail && (
                  <img src={link.thumbnail} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                )}
                <span style={{ flex: 1 }}>
                  {link.isProtected && <span style={{ marginRight: 6 }}>🔒</span>}
                  {!link.thumbnail && <span style={{ marginRight: 8 }}>{getLinkIcon(link.url)}</span>}
                  {link.title}
                </span>
                {link.isFeatured && <span style={{ fontSize: 11, opacity: 0.8 }}>↗</span>}
              </a>
            ))
          )}
        </div>

        {/* Email Subscribe */}
        <div style={{
          margin: '24px 0 8px',
          padding: '20px',
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: 'var(--link-bg)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            📧 Stay updated
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            Subscribe to get updates from {user?.name}
          </div>
          {subStatus === 'success' ? (
            <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 14 }}>
              ✅ {subMsg}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                placeholder="your@email.com"
                value={subEmail}
                onChange={e => setSubEmail(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1.5px solid rgba(128,128,128,0.5)',
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
              <button
                disabled={subStatus === 'loading'}
                onClick={async () => {
                  if (!subEmail) return;
                  setSubStatus('loading');
                  try {
                    await subscribeToProfile(username!, subEmail);
                    setSubStatus('success');
                    setSubMsg('Subscribed successfully!');
                  } catch (err: any) {
                    setSubStatus('error');
                    setSubMsg(err?.response?.data?.error || 'Something went wrong');
                    setTimeout(() => setSubStatus('idle'), 3000);
                  }
                }}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'var(--accent)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: subStatus === 'loading' ? 'not-allowed' : 'pointer',
                  opacity: subStatus === 'loading' ? 0.7 : 1,
                }}
              >
                {subStatus === 'loading' ? '...' : 'Subscribe'}
              </button>
            </div>
          )}
          {subStatus === 'error' && (
            <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{subMsg}</div>
          )}
        </div>

        {/* Footer */}
        {!user?.hideBranding && (
          <div className="profile-footer" style={{ color: 'var(--muted)' }}>
            Powered by <span style={{ color: 'var(--accent)' }}>mylinx</span>
          </div>
        )}

      </div>
    </div>

    {/* ── PASSWORD MODAL ── */}
    {pwdModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }} onClick={() => setPwdModal(null)}>
        <div style={{
          background: '#1c1c1c', borderRadius: 16, padding: 28,
          width: '100%', maxWidth: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 24, textAlign: 'center', marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 700, textAlign: 'center', color: '#fff', marginBottom: 4 }}>
            Protected Link
          </div>
          <div style={{ fontSize: 13, color: '#888', textAlign: 'center', marginBottom: 20 }}>
            Enter password to access "{pwdModal.title}"
          </div>
          <input
            type="password"
            placeholder="Enter password"
            value={pwdInput}
            autoFocus
            onChange={e => { setPwdInput(e.target.value); setPwdError(''); }}
            onKeyDown={async e => {
              if (e.key === 'Enter') {
                if (!pwdInput) return;
                setPwdLoading(true);
                try {
                  const res = await verifyLinkPassword(pwdModal.linkId, pwdInput);
                  sessionStorage.setItem(`unlocked_${pwdModal.linkId}`, 'true');
                  try { await trackLinkClick(pwdModal.linkId); } catch {}
                  setPwdModal(null);
                  window.open(res.data.url, '_blank');
                } catch {
                  setPwdError('Wrong password. Try again!');
                } finally { setPwdLoading(false); }
              }
            }}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 8,
              border: pwdError ? '1.5px solid #ef4444' : '1.5px solid #333',
              background: '#111', color: '#fff', fontSize: 14,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          {pwdError && (
            <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{pwdError}</div>
          )}
          <button
            disabled={pwdLoading}
            onClick={async () => {
              if (!pwdInput) return;
              setPwdLoading(true);
              try {
                const res = await verifyLinkPassword(pwdModal.linkId, pwdInput);
                sessionStorage.setItem(`unlocked_${pwdModal.linkId}`, 'true');
                try { await trackLinkClick(pwdModal.linkId); } catch {}
                setPwdModal(null);
                window.open(res.data.url, '_blank');
              } catch {
                setPwdError('Wrong password. Try again!');
              } finally { setPwdLoading(false); }
            }}
            style={{
              width: '100%', marginTop: 14, padding: '12px',
              borderRadius: 8, border: 'none',
              background: 'var(--accent, #C0392B)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              cursor: pwdLoading ? 'not-allowed' : 'pointer',
              opacity: pwdLoading ? 0.7 : 1,
            }}
          >
            {pwdLoading ? 'Checking...' : 'Unlock 🔓'}
          </button>
          <button onClick={() => setPwdModal(null)} style={{
            width: '100%', marginTop: 10, padding: '10px',
            borderRadius: 8, border: '1px solid #333',
            background: 'transparent', color: '#888',
            fontSize: 13, cursor: 'pointer',
          }}>
            Cancel
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default PublicProfile;